import { useState, useEffect } from "react";
import { ChevronDown, HelpCircle, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FAQPage() {
  const [faqs, setFaqs] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");

  useEffect(() => {
    axios.get(`${API}/faqs`).then(({ data }) => setFaqs(data)).catch(() => {});
  }, []);

  const categories = ["All", ...new Set(faqs.map((f) => f.category))];

  const filtered = faqs.filter((f) => {
    const matchCategory = activeCategory === "All" || f.category === activeCategory;
    const matchSearch = !search || f.question.toLowerCase().includes(search.toLowerCase()) || f.answer.toLowerCase().includes(search.toLowerCase());
    return matchCategory && matchSearch;
  });

  return (
    <div className="min-h-screen bg-white" data-testid="faq-page">
      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-20">
        <div className="container-custom text-center">
          <HelpCircle className="w-12 h-12 mx-auto mb-4 text-red-400" />
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">Frequently Asked Questions</h1>
          <p className="text-slate-300 max-w-2xl mx-auto text-lg">Find answers to common questions about our travel services, bookings, and policies.</p>
          <div className="max-w-md mx-auto mt-8 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              placeholder="Search questions..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-slate-400 h-12 rounded-full"
              data-testid="faq-search"
            />
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="container-custom py-16">
        {/* Category Tabs */}
        <div className="flex gap-2 flex-wrap mb-8 justify-center">
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setActiveCategory(c)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                activeCategory === c ? "bg-red-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {/* FAQ Accordion */}
        <div className="max-w-3xl mx-auto space-y-3">
          {filtered.length === 0 && (
            <p className="text-center text-slate-500 py-12">No FAQs found matching your search.</p>
          )}
          {filtered.map((faq) => (
            <div key={faq.id} className="border border-slate-200 rounded-xl overflow-hidden" data-testid={`faq-item-${faq.id}`}>
              <button
                className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-slate-50 transition-colors"
                onClick={() => setOpenId(openId === faq.id ? null : faq.id)}
                data-testid={`faq-toggle-${faq.id}`}
              >
                <span className="font-semibold text-slate-800 pr-4">{faq.question}</span>
                <ChevronDown className={`w-5 h-5 text-slate-400 flex-shrink-0 transition-transform ${openId === faq.id ? "rotate-180" : ""}`} />
              </button>
              {openId === faq.id && (
                <div className="px-6 pb-4 text-slate-600 leading-relaxed border-t border-slate-100 pt-3 animate-in slide-in-from-top-2 duration-200">
                  {faq.answer}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
